import { useAuth } from "@clerk/clerk-react";
import { Moment } from "moment";
import moment from "moment";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import {
  BentoBrand,
  BentoBrandSearchParameters,
  Campaign,
  CampaignStatus,
  SavedBrandCollection,
  Sort,
} from "schemas/dashboard";

import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";

import { OrganizationUserContext } from "./Organization";

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

export enum CampaignType {
  savedList = "savedList",
  brandSearch = "brandSearch",
  repitching = "repitching",
}

export const MAX_SELECT_BRANDS = 200;

export const DAYS_OF_THE_WEEK = [
  [6, "S"],
  [0, "M"],
  [1, "T"],
  [2, "W"],
  [3, "TH"],
  [4, "F"],
  [5, "S"],
];

interface NewCampaignBody {
  name: string;
  templateIds: number[];
  daysOfWeek: number[];
  timezone: string;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  bentoBrandIds?: number[];
  searchParameters?: BentoBrandSearchParameters;
  savedBrandCollectionId?: number;
  isAllSavedCollection?: boolean;
  perPage?: number;
  page?: number;
  status?: CampaignStatus;
  createdByAdmin?: boolean;
  overrideEmailLimit?: boolean;
  isRepitching?: boolean;
}

interface NewCampaignContextInterface {
  campaignTitle: string;
  setCampaignTitle: Dispatch<SetStateAction<string>>;
  selectedType: CampaignType | null;
  setSelectedType: Dispatch<SetStateAction<CampaignType | null>>;
  selectedCollection: SavedBrandCollection | null;
  setSelectedCollection: Dispatch<SetStateAction<SavedBrandCollection | null>>;
  selectAll: boolean;
  setSelectAll: Dispatch<SetStateAction<boolean>>;
  selectedBrandIds: Set<number>;
  setSelectedBrandIds: Dispatch<SetStateAction<Set<number>>>;
  templates: Template[];
  setTemplates: Dispatch<SetStateAction<Template[]>>;
  startDate: Moment | null;
  setStartDate: Dispatch<SetStateAction<Moment | null>>;
  endDate: Moment | null;
  setEndDate: Dispatch<SetStateAction<Moment | null>>;
  startTime: Moment | null;
  setStartTime: Dispatch<SetStateAction<Moment | null>>;
  endTime: Moment | null;
  setEndTime: Dispatch<SetStateAction<Moment | null>>;
  selectedDays: number[];
  setSelectedDays: Dispatch<SetStateAction<number[]>>;
  timezone: string;
  setTimezone: Dispatch<SetStateAction<string>>;
  currentSearchParams: BentoBrandSearchParameters;
  setCurrentSearchParams: Dispatch<SetStateAction<BentoBrandSearchParameters>>;
  searchUrlParams: string;
  setSearchUrlParams: Dispatch<SetStateAction<string>>;
  previewNewCampaign: (
    reset?: boolean,
    overrideEmailLimit?: boolean,
  ) => Promise<Response>;
  createNewCampaign: () => Promise<Campaign | null>;
  previewBrands: BentoBrand[];
  setPreviewBrands: Dispatch<SetStateAction<BentoBrand[]>>;
  previewPage: number;
  setPreviewPage: Dispatch<SetStateAction<number>>;
  previewCount: number | null;
  setPreviewCount: Dispatch<SetStateAction<number | null>>;
  status: CampaignStatus;
  setStatus: Dispatch<SetStateAction<CampaignStatus>>;
  excludedBentoBrandIds: Set<number>;
  setExcludedBentoBrandIds: Dispatch<SetStateAction<Set<number>>>;
}

const defaultInterface = {
  campaignTitle: "Untitled Campaign",
  setCampaignTitle: defaultContextMissingFunction,
  selectedType: null,
  setSelectedType: defaultContextMissingFunction,
  selectedCollection: null,
  setSelectedCollection: defaultContextMissingFunction,
  selectAll: false,
  setSelectAll: defaultContextMissingFunction,
  selectedBrandIds: new Set<number>(),
  setSelectedBrandIds: defaultContextMissingFunction,
  templates: [],
  setTemplates: defaultContextMissingFunction,
  startDate: null,
  setStartDate: defaultContextMissingFunction,
  endDate: null,
  setEndDate: defaultContextMissingFunction,
  startTime: null,
  setStartTime: defaultContextMissingFunction,
  endTime: null,
  setEndTime: defaultContextMissingFunction,
  selectedDays: [],
  setSelectedDays: defaultContextMissingFunction,
  timezone: "",
  setTimezone: defaultContextMissingFunction,
  currentSearchParams: {
    query: "",
    sort: Sort.Recommended,
  },
  setCurrentSearchParams: defaultContextMissingFunction,
  searchUrlParams: "",
  setSearchUrlParams: defaultContextMissingFunction,
  previewNewCampaign: defaultContextMissingFunction,
  createNewCampaign: defaultContextMissingFunction,
  previewBrands: [],
  setPreviewBrands: defaultContextMissingFunction,
  previewPage: 1,
  setPreviewPage: defaultContextMissingFunction,
  previewCount: null,
  setPreviewCount: defaultContextMissingFunction,
  status: CampaignStatus.in_progress,
  setStatus: defaultContextMissingFunction,
  excludedBentoBrandIds: new Set<number>(),
  setExcludedBentoBrandIds: defaultContextMissingFunction,
};

const NewCampaignContext =
  createContext<NewCampaignContextInterface>(defaultInterface);

interface NewCampaignProviderProps {
  children: React.ReactNode;
}

const getNextWeekday = (weekday: number): Moment => {
  const today = moment();
  const currentWeekday = today.isoWeekday(); // 1 (Mon) - 7 (Sun)
  const daysUntil = (7 + weekday - currentWeekday) % 7 || 7;
  return today.clone().add(daysUntil, "days").startOf("day");
};

const PREVIEW_PER_PAGE = 3;

const NewCampaignProvider = ({ children }: NewCampaignProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg, emailSettings } = useContext(OrganizationUserContext);
  const [campaignTitle, setCampaignTitle] =
    useState<string>("Untitled Campaign");
  const [selectedType, setSelectedType] = useState<CampaignType | null>(null);
  const [selectedCollection, setSelectedCollection] =
    useState<SavedBrandCollection | null>(null);
  const [currentSearchParams, setCurrentSearchParams] =
    useState<BentoBrandSearchParameters>({
      query: "",
      sort: Sort.Recommended,
    });
  const [searchUrlParams, setSearchUrlParams] = useState<string>("");
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedBrandIds, setSelectedBrandIds] = useState<Set<number>>(
    new Set(),
  );
  const [excludedBentoBrandIds, setExcludedBentoBrandIds] = useState<
    Set<number>
  >(new Set());
  const [previewBrands, setPreviewBrands] = useState<BentoBrand[]>([]);
  const [previewPage, setPreviewPage] = useState<number>(1);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [status, setStatus] = useState<CampaignStatus>(
    CampaignStatus.in_progress,
  );

  // Default dates
  const nextMonday = getNextWeekday(1);
  const mondayAfter = nextMonday.clone().add(7, "days");

  const [templates, setTemplates] = useState<Template[]>([]);
  const [startDate, setStartDate] = useState<Moment | null>(nextMonday);
  const [endDate, setEndDate] = useState<Moment | null>(mondayAfter);
  const [startTime, setStartTime] = useState<Moment | null>(
    nextMonday.clone().set({ hour: 9, minute: 0, second: 0 }),
  );
  const [endTime, setEndTime] = useState<Moment | null>(
    mondayAfter.clone().set({ hour: 15, minute: 0, second: 0 }),
  );
  const [selectedDays, setSelectedDays] = useState<number[]>([0, 1, 2, 3]);
  const [timezone, setTimezone] = useState<string>(
    emailSettings?.timezone || moment.tz.guess(),
  );

  const constructPostBody = () => {
    const body: NewCampaignBody = {
      name: campaignTitle,
      templateIds: templates
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined),
      daysOfWeek: selectedDays,
      timezone,
      startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      startTime: startTime ? startTime.format("HH:mm:ss") : null,
      endTime: endTime ? endTime.format("HH:mm:ss") : null,
      status,
      createdByAdmin: Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN)),
    };

    if (selectedBrandIds.size > 0) {
      body.bentoBrandIds = Array.from(selectedBrandIds);
    }

    if (
      selectedType === CampaignType.brandSearch ||
      selectedType === CampaignType.repitching
    ) {
      body.searchParameters = currentSearchParams;
      if (selectedType === CampaignType.repitching) {
        body.isRepitching = true;
      }
    } else {
      if (selectedCollection && selectedCollection?.id <= 0) {
        body.isAllSavedCollection = true;
      } else {
        body.savedBrandCollectionId = selectedCollection?.id;
      }
    }

    return body;
  };

  const previewNewCampaign = async (
    reset: boolean = false,
    overrideEmailLimit: boolean = false,
  ) => {
    if (!currentOrg?.id) {
      return;
    }

    const body = constructPostBody();
    if (reset) {
      body.page = 1;
      setPreviewPage(1);
    } else {
      body.page = previewPage;
    }
    body.perPage = PREVIEW_PER_PAGE;
    body.overrideEmailLimit = overrideEmailLimit;

    return await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/campaigns/preview`,
      "POST",
      {},
      body,
      true,
    );
  };

  const createNewCampaign = async () => {
    if (!currentOrg?.id) {
      return null;
    }

    const body = constructPostBody();

    try {
      const { campaign } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns`,
        "POST",
        {},
        body,
      );
      return campaign;
    } catch (error) {
      throw error;
    }
  };

  return (
    <NewCampaignContext.Provider
      value={{
        campaignTitle,
        setCampaignTitle,
        selectedType,
        setSelectedType,
        selectedCollection,
        setSelectedCollection,
        selectAll,
        setSelectAll,
        selectedBrandIds,
        setSelectedBrandIds,
        templates,
        setTemplates,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        selectedDays,
        setSelectedDays,
        timezone,
        setTimezone,
        currentSearchParams,
        setCurrentSearchParams,
        searchUrlParams,
        setSearchUrlParams,
        previewNewCampaign,
        createNewCampaign,
        previewBrands,
        setPreviewBrands,
        previewPage,
        setPreviewPage,
        previewCount,
        setPreviewCount,
        status,
        setStatus,
        excludedBentoBrandIds,
        setExcludedBentoBrandIds,
      }}
    >
      {children}
    </NewCampaignContext.Provider>
  );
};

export { NewCampaignProvider, NewCampaignContext };
